import { createRouter, createWebHistory } from 'vue-router'

import { authRoutes } from '@/modules/auth/routes'
import { adminRoutes } from '@/modules/admin/routes'
import { onboardingRoutes } from '@/modules/onboarding/routes'
import { studioRoutes } from '@/modules/studio/routes'
import { studentsRoutes } from '@/modules/students/routes'
import { schedulingRoutes } from '@/modules/scheduling/routes'
import { reservationsRoutes } from '@/modules/reservations/routes'
import { paymentsRoutes } from '@/modules/payments/routes'
import { productsRoutes } from '@/modules/products/routes'
import { coachesRoutes } from '@/modules/coaches/routes'
import { settingsRoutes } from '@/modules/settings/routes'
import { whatsappRoutes } from '@/modules/whatsapp/routes'
import { integrationsRoutes } from '@/modules/integrations/routes'
import { externalReservationsRoutes } from '@/modules/external-reservations/routes'
import { setupGuards } from './guards'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'dashboard',
      component: () => import('@/modules/dashboard/views/DashboardView.vue'),
      meta: { requiresAuth: true, roles: ['owner', 'admin', 'staff'] },
    },
    {
      path: '/dashboard/insights',
      name: 'dashboard-insights',
      component: () => import('@/modules/dashboard/views/InsightsView.vue'),
      meta: { requiresAuth: true, roles: ['owner', 'admin'] },
    },
    ...authRoutes,
    ...adminRoutes,
    ...onboardingRoutes,
    ...studioRoutes,
    ...studentsRoutes,
    ...schedulingRoutes,
    ...reservationsRoutes,
    ...paymentsRoutes,
    ...productsRoutes,
    ...coachesRoutes,
    ...settingsRoutes,
    ...whatsappRoutes,
    ...integrationsRoutes,
    ...externalReservationsRoutes,
  ],
})

setupGuards(router)

const CHUNK_RELOAD_SESSION_KEY = 'flowestudio:chunk-reload-path'

const isDynamicImportFailure = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|ChunkLoadError/i.test(
    message,
  )
}

router.onError((error, to) => {
  if (!isDynamicImportFailure(error)) return

  const targetPath = to?.fullPath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`
  const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) === targetPath

  if (alreadyReloaded) {
    sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY)
    console.error('[router] Dynamic import failed after one forced reload.', error)
    return
  }

  sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, targetPath)
  window.location.assign(targetPath)
})

export default router
